// src/core/services/payments/stripeService.ts
// Integração com Stripe para pagamentos

export type PaymentStatus = 'pending' | 'processing' | 'succeeded' | 'failed' | 'canceled' | 'refunded';
export type SubscriptionStatus = 'active' | 'past_due' | 'canceled' | 'incomplete' | 'trialing';

export interface PaymentIntent {
  id: string;
  amount: number;
  currency: string;
  status: PaymentStatus;
  clientSecret: string;
  metadata?: Record<string, string>;
}

export interface Subscription {
  id: string;
  customerId: string;
  priceId: string;
  status: SubscriptionStatus;
  currentPeriodStart: Date;
  currentPeriodEnd: Date;
  cancelAtPeriodEnd: boolean;
}

export interface Customer {
  id: string;
  email: string;
  name?: string;
  phone?: string;
  defaultPaymentMethod?: string;
}

export interface Plan {
  id: string;
  name: string;
  description: string;
  priceMonthly: number;
  priceYearly: number;
  features: string[];
  stripePriceIdMonthly: string;
  stripePriceIdYearly: string;
  highlighted?: boolean;
}

// Planos do NossaPesca
export const PESCAR_PLANS: Plan[] = [
  {
    id: 'starter',
    name: 'Starter',
    description: 'Ideal para começar',
    priceMonthly: 97,
    priceYearly: 970,
    features: [
      '1 Site publicado',
      'Até 500 visitas/mês',
      '5 GB de armazenamento',
      'Suporte por email',
      'Templates básicos',
    ],
    stripePriceIdMonthly: 'price_starter_monthly',
    stripePriceIdYearly: 'price_starter_yearly',
  },
  {
    id: 'professional',
    name: 'Professional',
    description: 'Para negócios em crescimento',
    priceMonthly: 197,
    priceYearly: 1970,
    features: [
      '1 Site publicado',
      'Visitas ilimitadas',
      '25 GB de armazenamento',
      'Suporte prioritário',
      'Todos os templates',
      'Domínio personalizado',
      'Analytics avançado',
      'Integração WhatsApp',
    ],
    stripePriceIdMonthly: 'price_professional_monthly',
    stripePriceIdYearly: 'price_professional_yearly',
    highlighted: true,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    description: 'Solução completa',
    priceMonthly: 397,
    priceYearly: 3970,
    features: [
      'Sites ilimitados',
      'Visitas ilimitadas',
      '100 GB de armazenamento',
      'Suporte 24/7',
      'Templates exclusivos',
      'Domínio personalizado',
      'Analytics avançado',
      'API completa',
      'White-label total',
      'Onboarding dedicado',
    ],
    stripePriceIdMonthly: 'price_enterprise_monthly',
    stripePriceIdYearly: 'price_enterprise_yearly',
  },
];

class StripePaymentService {
  private apiUrl = '/api/payments'; // Backend endpoint

  /**
   * Criar cliente no Stripe
   */
  async createCustomer(data: {
    email: string;
    name?: string;
    phone?: string;
    tenantId: string;
  }): Promise<Customer> {
    const response = await fetch(`${this.apiUrl}/customers`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error('Failed to create customer');
    }

    return response.json();
  }

  /**
   * Criar Payment Intent para pagamento único
   */
  async createPaymentIntent(data: {
    amount: number; // em centavos
    currency?: string;
    customerId?: string;
    description?: string;
    metadata?: Record<string, string>;
  }): Promise<PaymentIntent> {
    const response = await fetch(`${this.apiUrl}/payment-intents`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...data,
        currency: data.currency || 'brl',
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to create payment intent');
    }

    return response.json();
  }

  /**
   * Criar assinatura
   */
  async createSubscription(data: {
    customerId: string;
    priceId: string;
    trialDays?: number;
    metadata?: Record<string, string>;
  }): Promise<{ subscriptionId: string; clientSecret: string }> {
    const response = await fetch(`${this.apiUrl}/subscriptions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error('Failed to create subscription');
    }

    return response.json();
  }

  /**
   * Cancelar assinatura
   */
  async cancelSubscription(subscriptionId: string, immediate = false): Promise<Subscription> {
    const response = await fetch(`${this.apiUrl}/subscriptions/${subscriptionId}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ immediate }),
    });

    if (!response.ok) {
      throw new Error('Failed to cancel subscription');
    }

    return response.json();
  }

  /**
   * Obter assinatura atual
   */
  async getSubscription(subscriptionId: string): Promise<Subscription> {
    const response = await fetch(`${this.apiUrl}/subscriptions/${subscriptionId}`);

    if (!response.ok) {
      throw new Error('Failed to get subscription');
    }

    return response.json();
  }

  /**
   * Listar faturas do cliente
   */
  async listInvoices(customerId: string): Promise<Array<{
    id: string;
    amount: number;
    status: string;
    pdfUrl: string;
    createdAt: Date;
  }>> {
    const response = await fetch(`${this.apiUrl}/customers/${customerId}/invoices`);

    if (!response.ok) {
      throw new Error('Failed to list invoices');
    }

    return response.json();
  }

  /**
   * Atualizar método de pagamento padrão
   */
  async updatePaymentMethod(customerId: string, paymentMethodId: string): Promise<void> {
    const response = await fetch(`${this.apiUrl}/customers/${customerId}/payment-method`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ paymentMethodId }),
    });

    if (!response.ok) {
      throw new Error('Failed to update payment method');
    }
  }

  /**
   * Criar portal de billing do cliente
   */
  async createBillingPortalSession(customerId: string): Promise<{ url: string }> {
    const response = await fetch(`${this.apiUrl}/billing-portal`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ customerId }),
    });

    if (!response.ok) {
      throw new Error('Failed to create billing portal session');
    }

    return response.json();
  }

  /**
   * Criar sessão de checkout
   */
  async createCheckoutSession(data: {
    priceId: string;
    mode: 'payment' | 'subscription';
    successUrl: string;
    cancelUrl: string;
    customerId?: string;
    metadata?: Record<string, string>;
  }): Promise<{ sessionId: string; url: string }> {
    const response = await fetch(`${this.apiUrl}/checkout-sessions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error('Failed to create checkout session');
    }

    return response.json();
  }

  /**
   * Verificar status do webhook
   */
  async verifyWebhookEvent(payload: string, signature: string): Promise<{
    type: string;
    data: unknown;
  }> {
    const response = await fetch(`${this.apiUrl}/webhooks/verify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Stripe-Signature': signature,
      },
      body: payload,
    });

    if (!response.ok) {
      throw new Error('Webhook verification failed');
    }

    return response.json();
  }
}

// Singleton
export const stripeService = new StripePaymentService();

// === Componentes Auxiliares ===

/**
 * Formata valor em centavos para exibição
 */
export function formatPrice(amountInCents: number, currency = 'BRL'): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency,
  }).format(amountInCents / 100);
}

/**
 * Calcula economia do plano anual
 */
export function calculateYearlySavings(plan: Plan): {
  savings: number;
  percentage: number;
} {
  const monthlyTotal = plan.priceMonthly * 12;
  const savings = monthlyTotal - plan.priceYearly;
  const percentage = Math.round((savings / monthlyTotal) * 100);
  
  return { savings, percentage };
}

/**
 * Obtém plano por ID
 */
export function getPlanById(planId: string): Plan | undefined {
  return PESCAR_PLANS.find(p => p.id === planId);
}

/**
 * Hook para gerenciar pagamentos
 */
export function usePayments() {
  const createPayment = async (amount: number, metadata?: Record<string, string>) => {
    return stripeService.createPaymentIntent({
      amount: amount * 100, // Converter para centavos
      metadata,
    });
  };

  const subscribe = async (planId: string, isYearly: boolean, customerId: string) => {
    const plan = getPlanById(planId);
    if (!plan) throw new Error('Plan not found');

    const priceId = isYearly ? plan.stripePriceIdYearly : plan.stripePriceIdMonthly;
    return stripeService.createSubscription({ customerId, priceId });
  };

  const checkout = async (planId: string, isYearly: boolean) => {
    const plan = getPlanById(planId);
    if (!plan) throw new Error('Plan not found');

    const priceId = isYearly ? plan.stripePriceIdYearly : plan.stripePriceIdMonthly;
    return stripeService.createCheckoutSession({
      priceId,
      mode: 'subscription',
      successUrl: `${window.location.origin}/admin/subscription/success`,
      cancelUrl: `${window.location.origin}/admin/subscription/canceled`,
    });
  };

  const openBillingPortal = async (customerId: string) => {
    const { url } = await stripeService.createBillingPortalSession(customerId);
    window.location.href = url;
  };

  return {
    plans: PESCAR_PLANS,
    createPayment,
    subscribe,
    checkout,
    openBillingPortal,
    formatPrice,
    calculateYearlySavings,
  };
}
